import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import useCasesData from '../data/useCases.json';
import PageHero from '../components/PageHero';

const UseCases = () => {
  const [activeCase, setActiveCase] = useState(useCasesData.cases[0].id);
  const activeData = useCasesData.cases.find(c => c.id === activeCase);

  return (
    <div className="relative">
      <PageHero 
        title={useCasesData.pageTitle}
        subtitle={useCasesData.pageSubtitle}
      />

      {/* Use Case Selector */}
      <section className="section-padding pt-0">
        <div className="max-w-7xl mx-auto">
          {/* Tabs */}
          <div className="flex flex-wrap gap-3 mb-12" data-testid="use-case-tabs">
            {useCasesData.cases.map((useCase) => {
              const IconComponent = Icons[useCase.icon];
              return (
                <button
                  key={useCase.id}
                  onClick={() => setActiveCase(useCase.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 ${
                    activeCase === useCase.id
                      ? 'bg-violet-500 text-white neon-glow'
                      : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white border border-white/10'
                  }`}
                  data-testid={`use-case-tab-${useCase.id}`}
                >
                  {IconComponent && <IconComponent size={18} />}
                  <span className="font-medium">{useCase.title}</span>
                </button>
              );
            })}
          </div>

          {/* Active Use Case Detail */}
          {activeData && (
            <div 
              className="grid lg:grid-cols-2 gap-12 items-start"
              data-testid={`use-case-detail-${activeData.id}`}
            >
              <div>
                <div className={`w-16 h-16 rounded-2xl ${
                  activeData.color === 'magenta' ? 'bg-pink-500/10 text-pink-400' : 'bg-violet-500/10 text-violet-400'
                } flex items-center justify-center mb-6`}>
                  {Icons[activeData.icon] && React.createElement(Icons[activeData.icon], { size: 32 })}
                </div>
                
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  {activeData.title}
                </h2>
                <p className="text-lg text-slate-500 mb-6">{activeData.subtitle}</p>
                <p className="text-slate-400 leading-relaxed text-lg">
                  {activeData.description}
                </p>
              </div>

              <div className="glass-panel p-8">
                <h3 className="text-lg font-semibold text-white mb-6">Key Capabilities</h3>
                <ul className="space-y-4">
                  {activeData.highlights.map((highlight, index) => (
                    <li 
                      key={index}
                      className="flex items-start gap-3"
                    >
                      <div className={`w-6 h-6 rounded-full ${
                        activeData.color === 'magenta' ? 'bg-pink-500/20 text-pink-400' : 'bg-violet-500/20 text-violet-400'
                      } flex items-center justify-center shrink-0 mt-0.5`}>
                        <Icons.Check size={14} />
                      </div>
                      <span className="text-slate-300">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* All Use Cases Grid */}
      <section className="section-padding cosmic-bg">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">
            All Use Cases
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {useCasesData.cases.map((useCase) => {
              const IconComponent = Icons[useCase.icon];
              return (
                <button
                  key={useCase.id}
                  onClick={() => {
                    setActiveCase(useCase.id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`glass-panel p-8 text-left group transition-all duration-300 hover:border-violet-500/30 ${
                    activeCase === useCase.id ? 'border-violet-500/30' : ''
                  }`}
                  data-testid={`use-case-grid-${useCase.id}`}
                >
                  <div className={`w-12 h-12 rounded-xl ${
                    useCase.color === 'magenta' ? 'bg-pink-500/10 text-pink-400' : 'bg-violet-500/10 text-violet-400'
                  } flex items-center justify-center mb-4`}>
                    {IconComponent && <IconComponent size={24} />}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-violet-300 transition-colors">
                    {useCase.title}
                  </h3>
                  <p className="text-slate-400">{useCase.subtitle}</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Which mode will you try first?
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            ROMIND adapts to your workflow. Join early access to explore all use cases.
          </p>
          <a 
            href="/#early-access" 
            className="btn-primary text-lg px-10 py-4"
            data-testid="use-cases-cta"
          >
            Get Early Access
          </a>
        </div>
      </section>
    </div>
  );
};

export default UseCases;
